import { useState, type ChangeEvent, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

/** Controlled text fields with field-level validation; native required/type constraints remain active. */
export function useTextForm<T extends Record<string, string>>({
    initialValues,
    validate,
}: {
    initialValues: T;
    validate?: (values: T) => Partial<Record<keyof T, string>>;
}) {
    const { t } = useTranslation();
    const [formError, setFormError] = useState<string | undefined>();
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
    return {
        values,
        formError,
        reset: () => {
            setValues(initialValues);
            setErrors({});
            setFormError(undefined);
        },
        getInputProps: (name: keyof T) => ({
            name: String(name),
            value: values[name],
            error: errors[name],
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
                const value = event.currentTarget.value;
                setValues((previous) => ({ ...previous, [name]: value }));
                setErrors((previous) => ({ ...previous, [name]: undefined }));
            },
        }),
        onSubmit: (submit: (values: T) => void | Promise<void>) => (event: SubmitEvent<HTMLFormElement>) => {
            event.preventDefault();
            const nextErrors = validate?.(values) ?? {};
            setErrors(nextErrors);
            setFormError(undefined);
            const invalid = Object.entries(nextErrors).filter(([, error]) => Boolean(error));
            if (invalid.length > 0) {
                const form = event.currentTarget;
                const invalidField = invalid
                    .map(([name]) => form.elements.namedItem(name))
                    .find((field) => field instanceof HTMLElement);
                if (invalidField instanceof HTMLElement) invalidField.focus();
                // Schema-level issues have no input to attach to: surface them at the form level.
                const unbound = invalid.filter(([name]) => !form.elements.namedItem(name));
                if (unbound.length > 0) setFormError([...new Set(unbound.map(([, error]) => error))].join(' '));
                return;
            }
            void Promise.resolve()
                .then(() => submit(values))
                .catch(() => setFormError(t('platform.errors.internal')));
        },
    };
}
